import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filterDashboardData, setDashboardData } from '../../store/actions/dashboardActions';
import { selectSliceDashboard, selectSliceDashboardDisplayItems } from '../../store/reducers/dashboardReducer';
import { RecommendationStatus } from '../../utils/Recommendation';
import useAuthUtils from '../../utils/useAuthUtils';
import useMessages from '../../utils/useMessages';
import RequestCard from './RequestCard/RequestCard';
import Sidebar from './Sidebar/Sidebar';
import BusyCover from '../../components/controls/BusyCover/BusyCover';
import { apiGET, apiPUT } from '../../utils/networking';
import Heading from '../../components/controls/Typography/Heading';
import { Messages } from '../components/messages/Messages';
import { ViewportList } from 'react-viewport-list';

import './Dashboard.less';

const USE_VIRTUAL_LIST_ITEMS_THRESHOLD = 50;

export const Dashboard = () => {
  const auth = useAuthUtils();
  const messages = useMessages();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const storeDashboard = useSelector(selectSliceDashboard);
  const storeDashboardItems = useSelector(selectSliceDashboardDisplayItems);
  const ref = useRef(null);

  /**
   *
   * @param {ReturnType<import('../../store/reducers/dashboardReducer').selectSliceDashboardDisplayItems>[number]} requestInfo
   * @param {Number} idx
   * @returns
   */
  const createRequestCard = (requestInfo, idx) => {
    return (
      <RequestCard
        request={requestInfo.request}
        isReusedForFutureEvaluation={requestInfo.isReusedForFutureEvaluation}
        disableReuseEvaluation={requestInfo.disableReuseEvaluation}
        autoFiledRequests={requestInfo.autoFiledRequests}
        key={requestInfo.request.id}
        expandedState={storeDashboard.viewParams.allExpanded}
        highlightWords={requestInfo.highlightWords}
        onChanged={() => {
          fetchDashboard();
        }}
      />
    );
  };

  const render = () => {
    const statusCount = storeDashboard.dashboardData?.statusCount ?? {};
    const hasActiveRequests =
      statusCount[RecommendationStatus.REQUESTED] > 0 || statusCount[RecommendationStatus.ACCEPTED] > 0;

    const noDataMessage =
      !loading && storeDashboardItems?.length === 0
        ? hasActiveRequests
          ? 'You have no requests to view based on the filters you selected.'
          : 'You have no open requests associated with your account.'
        : null;

    return (
      <main className="lsn-page lsn-page-dashboard">
        <div className="link-jump-to-content">
          <a className="skip-to-content-link" href="#recommendations">
            Skip to recommendations
          </a>
        </div>
        <div className="lsn-container">
          <BusyCover isBusy={loading} />
          <div className="columns evaluations-header-wrapper">
            <Heading
              className="column col-2 col-xl-3 col-md-12 text-left no-focus-outline"
              aria-label={`Dashboard Requests`}
              tabIndex={0}
            >
              Requests
            </Heading>
            <div className="column col-10 col-xl-9 col-md-12 notifications">
              <Messages />
            </div>
          </div>
          <div className="columns evaluations-wrapper">
            <Sidebar classNames={'column col-2 col-xl-3 col-md-12'} />
            <div className="column col-10 col-xl-9 col-md-12" id="recommendations">
              {noDataMessage ? <p className="lsn-no-dashboard-requests">{noDataMessage}</p> : void 0}
              <div className="scroll-container" ref={ref}>
                {(storeDashboardItems?.length ?? 0) < USE_VIRTUAL_LIST_ITEMS_THRESHOLD ? (
                  storeDashboardItems?.map(createRequestCard)
                ) : (
                  <ViewportList
                    viewportRef={ref}
                    items={[...storeDashboardItems, ...storeDashboardItems, ...storeDashboardItems]}
                    initialPrerender={USE_VIRTUAL_LIST_ITEMS_THRESHOLD}
                  >
                    {(item, index) => createRequestCard(item, index)}
                  </ViewportList>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const resp = await apiGET('/recommendation/rest/dashboard');
      dispatch(setDashboardData(resp.body));
      return resp;
    } catch (error) {
      console.error(error);
      messages.clear().notifyRed(`Cannot fetch your dashboard. Please try again or contact help for assistance.`);
    } finally {
      setLoading(false);
    }
  };

  // Complicated logic to handle re-use flow
  // and/or fetch dashboard data
  useEffect(() => {
    auth.flows.reUseEvalGuest.isActive()
      ? apiPUT(
          `/recommendation/rest/evaluationRequests/${auth.flows.reUseEvalGuest.data?.evalRequestId}/reuseForFutureEvaluations`
        )
          .then(() => {
            // show closed evaluations when re-use flow occurs
            dispatch(
              filterDashboardData({
                filterByStatus: [...storeDashboard.viewParams.filterByStatus, RecommendationStatus.COMPLETED],
              })
            );
          })
          .catch(error => {
            console.error(error);
          })
          .finally(() => {
            fetchDashboard();
            // clear re-use flow
            auth.flows.reUseEvalGuest.clear();
          })
      : fetchDashboard();
  }, []);

  // Set the document title
  useEffect(() => {
    document.title = 'Letters Portal - Dashboard';
  }, []);

  return render();
};

export default Dashboard;
